import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  MutableRefObject,
} from 'react';
import { useToast } from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import { StatusTypeProps } from '../components';
import { Sse } from '../helpers';
import { RequestMethods, useLogger, useRequest, useVariables } from '../hooks';
import {
  useDispatchers,
  Dispatchers,
  ActionRequest,
  initialStateOperationStarted,
  initialStateIdCaptured,
  initialStateSelfieCaptured,
  initialStateFacialAuthentication,
  initialStateFacialExtraction,
  initialStatePassiveLiveness,
  initialStateIdValiated,
  initialStateCivilRegistry,
  initialStatePreviousOnboarding,
  initialStateOperationFinished,
  initialStateOperationExpired,
  initialStateDocumentSignature,
  initialStatePersonalInformationGathered,
  StateRequest,
  ActionTypeRequest,
} from '../reducers';
import {
  endPoints,
  FacialAuthenticationStatus,
  FacialLivenessDiagnostic,
  SignedDocument,
  SSE_CONNECTION_TIMEOUT,
  SseEvents,
  SseResultObject,
  SseStatus,
  StatusOperations,
  ReloadResultsResponseDto,
  WEB_SOURCE,
  POLLING_ATTEMPTS_LIMIT,
  DEMOS_BE,
} from '../states';
import { capitalize, pascalize } from '../utils';
import { useAuth } from './AuthProvider';

type IProps = {
  children: React.ReactNode;
  url: string;
};

type ContextProps = {
  url: string;
  error: boolean;
  reloadError: boolean;
  connection: MutableRefObject<EventSource | null | undefined> | null;
  status?: SseStatus;
  connect(operationId: string): void;
  disconnect(): void;
  resetStates(): void;
  clearPolling(): void;
  getOperationResults(operationId: string): void;
  setIsOperationFinished: Dispatch<SetStateAction<boolean>>;
  dispatchDocumentSignature: Dispatch<ActionTypeRequest<SignedDocument>>;
  timeoutError: boolean;
  operationStartedState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.OPERATION_STARTED];
    status?: StatusTypeProps;
  };
  idCapturedState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.ID_CAPTURED];
    status?: StatusTypeProps;
  };
  selfieCapturedState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.SELFIE_CAPTURED];
    status?: StatusTypeProps;
  };
  facialAuthenticationState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.FACIAL_AUTHENTICATION_EVALUATED];
    status?: StatusTypeProps;
  };
  facialExtractionState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.FACIAL_EXTRACTION_EVALUATED];
    status?: StatusTypeProps;
  };
  passiveLivenessState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.PASSIVE_LIVENESS_EVALUATED];
    status?: StatusTypeProps;
  };
  idValidatedState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.ID_VALIDATED];
    status?: StatusTypeProps;
  };
  civilRegistryState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.CIVIL_REGISTRY_VALIDATED];
    status?: StatusTypeProps;
  };
  previousOnboardingState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.PREVIOUS_ONBOARDING_VALIDATED];
    status?: StatusTypeProps;
  };
  operationFinishedState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.OPERATION_FINISHED];
    status?: StatusTypeProps;
  };
  operationExpiredState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.OPERATION_EXPIRED];
    status?: StatusTypeProps;
  };
  documentSignatureState: {
    loading: boolean;
    error: boolean;
    data?: SignedDocument;
    status?: StatusTypeProps;
  };
  personalInformationState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.PERSONAL_INFORMATION_GATHERED];
    status?: StatusTypeProps;
  };
};

const Ctx = createContext<ContextProps>({
  url: '',
  error: false,
  reloadError: false,
  connection: null,
  connect: () => {},
  disconnect: () => {},
  resetStates: () => {},
  clearPolling: () => {},
  getOperationResults: () => {},
  setIsOperationFinished: () => {},
  dispatchDocumentSignature: () => {},
  timeoutError: false,
  operationStartedState: initialStateOperationStarted,
  idCapturedState: initialStateIdCaptured,
  selfieCapturedState: initialStateSelfieCaptured,
  facialAuthenticationState: initialStateFacialAuthentication,
  facialExtractionState: initialStateFacialExtraction,
  passiveLivenessState: initialStatePassiveLiveness,
  idValidatedState: initialStateIdValiated,
  civilRegistryState: initialStateCivilRegistry,
  previousOnboardingState: initialStatePreviousOnboarding,
  operationFinishedState: initialStateOperationFinished,
  operationExpiredState: initialStateOperationExpired,
  documentSignatureState: initialStateDocumentSignature,
  personalInformationState: initialStatePersonalInformationGathered,
});

export const DataProvider = ({ children, url }: IProps) => {
  const { dispatchers, cardStates } = useDispatchers();
  const { t } = useTranslation();
  const { toastManager } = useToast();
  const { token, tenantDefault } = useAuth();
  const { apiDemoUrl, appTesting, wiremockUrl, isCanada } = useVariables();
  const { requestBasic: demoRequest } = useRequest(
    !appTesting ? apiDemoUrl : wiremockUrl
  );
  const { captureException } = useLogger();

  const [error, setError] = useState<boolean>(false);
  const [reloadError, setReloadError] = useState<boolean>(false);
  const [timeoutError, setTimeoutError] = useState<boolean>(false);
  const [isOperationFinished, setIsOperationFinished] =
    useState<boolean>(false);
  const [pollingAttempts, setPollingAttempts] = useState<number>(0);
  const [status, setStatus] = useState<SseStatus>(SseStatus.connecting);
  const connection = useRef<EventSource | null>();
  const timeoutId = useRef<NodeJS.Timeout | null>();
  const intervalId = useRef<NodeJS.Timer | null>();
  const isDemosBe = url?.includes(DEMOS_BE);

  const {
    operationStarted,
    idCaptured,
    selfieCaptured,
    facialAuthenticationEvaluated,
    facialExtractionEvaluated,
    passiveLivenessEvaluated,
    idValidated,
    civilRegistryValidated,
    previousOnboardingValidated,
    operationFinished,
    operationExpired,
    documentSignature,
    personalInformationGathered,
  } = cardStates;

  const toastError = () => {
    toastManager({
      type: 'error',
      message: t('There was a server error'),
      duration: 2000,
    });
    setError(true);
  };

  const checkStatus = <T,>(
    item: StateRequest<T>,
    checkSuccess: () => boolean,
    checkError?: () => boolean
  ): StatusOperations => {
    return (!item.data && !item.error) || item.loading
      ? StatusOperations.STARTED
      : item.error || (checkError && checkError())
      ? StatusOperations.CONNECTION_ERROR
      : checkSuccess()
      ? StatusOperations.SUCCEEDED
      : StatusOperations.DENIED;
  };

  const dispatchResults = (event: MessageEvent) => {
    const dispatcher =
      dispatchers[`dispatch${pascalize(event.type, '_')}` as keyof Dispatchers];

    if (dispatcher)
      dispatcher({
        type: ActionRequest.SET_DATA,
        payload: JSON.parse(event.data),
      });
  };

  const handleTimer = (event?: MessageEvent) => {
    if (event && timeoutId.current) clearTimeout(timeoutId.current);

    if (!appTesting)
      timeoutId.current = setTimeout(() => {
        setStatesError();
        setTimeoutError(true);
        disconnect();
      }, SSE_CONNECTION_TIMEOUT);
  };

  const setStatesError = () => {
    Object.keys(cardStates).forEach((state: string) => {
      if (!cardStates[state as keyof typeof cardStates].data) {
        const capitalizedState = capitalize(state);
        const dispatcherKey =
          `dispatch${capitalizedState}` as keyof Dispatchers;

        if (dispatchers[dispatcherKey])
          dispatchers[dispatcherKey]({
            type: ActionRequest.SET_ERROR,
          });
      }
    });
  };

  const resetStates = (resetSignature = true) => {
    setReloadError(false);
    Object.keys(cardStates).forEach((state: string) => {
      const capitalizedState = capitalize(state);
      const dispatcherKey = `dispatch${capitalizedState}` as keyof Dispatchers;

      if (capitalizedState === 'DocumentSignature' && !resetSignature) return;
      if (dispatchers[dispatcherKey]) {
        let type = ActionRequest.START_REQUEST;
        if (dispatcherKey === 'dispatchDocumentSignature') {
          type = ActionRequest.RESET;
        }
        dispatchers[dispatcherKey]({
          type,
        });
      }
    });
  };

  const connect = (operationId: string) => {
    setError(false);
    if (operationId && token) {
      connection.current = Sse({
        url: `${url}/sse${isDemosBe ? `/${operationId}?token=${token}` : ''}`,
        setStatus,
        toastError,
        dispatchResults,
        setIsOperationFinished,
        setError,
        handleTimer,
      });
      handleTimer();
    } else {
      toastError();
    }
  };

  const getOperationResults = (operationId: string) => {
    disconnect();
    resetStates();

    const fetchResults = async () => {
      setPollingAttempts((attempts) => attempts + 1);

      try {
        const responseResults: ReloadResultsResponseDto = await demoRequest(
          `${endPoints.Workflows.Results}${
            !appTesting ? '/' + operationId : ''
          }`,
          {
            method: RequestMethods.get,
            headers: {
              'Content-Type': 'application/json',
              'x-source': WEB_SOURCE,
              'x-operation-id': operationId,
              'x-demo-app-version': '1',
              'x-tenant-id': tenantDefault,
            },
          }
        );
        if (
          responseResults &&
          responseResults.operation_finished?.status !==
            StatusOperations.STARTED
        ) {
          clearPolling();
          const resultKeys = Object.keys(responseResults).filter(
            (item) => item.toUpperCase() in SseEvents
          );
          for (const item of resultKeys) {
            const pascalizedState = pascalize(item, '_');
            const dispatcherKey =
              `dispatch${pascalizedState}` as keyof Dispatchers;
            dispatchers[dispatcherKey]({
              type: ActionRequest.SET_DATA,
              payload: responseResults[item as keyof ReloadResultsResponseDto],
            });
          }
        }
      } catch (e: unknown) {
        clearPolling();
        captureException(e as Error, {
          operation: 'getOperationResults',
          url: endPoints.Workflows.Results,
          operationId: operationId,
        });
        setStatesError();
        setReloadError(true);
      }
    };

    fetchResults();
    intervalId.current = setInterval(() => {
      fetchResults();
    }, 20000);
  };

  const disconnect = () => {
    connection.current?.close();
    connection.current = null;
  };

  const clearPolling = () => {
    setPollingAttempts(0);
    if (intervalId?.current) {
      clearInterval(intervalId.current);
      intervalId.current = null;
    }
  };

  const morphologicalStatus = useMemo(() => {
    let status = checkStatus(idValidated, () => !!idValidated.data?.succeed);
    if (
      [
        StatusOperations.DENIED,
        StatusOperations.CONNECTION_ERROR,
        StatusOperations.SUCCEEDED,
      ].includes(status) &&
      isCanada
    ) {
      status = StatusOperations.CHECK_PLATFORM;
    }
    return status;
  }, [idValidated, isOperationFinished]);

  const operationFinishStatus = useMemo(() => {
    let status = checkStatus(
      operationFinished,
      () => operationFinished.data?.status === StatusOperations.SUCCEEDED,
      () => operationFinished.data?.status === StatusOperations.ERROR
    );
    if (reloadError) status = StatusOperations.CHECK_PLATFORM;
    return status;
  }, [reloadError, operationFinished]);

  useEffect(() => {
    if (status === SseStatus.error || isOperationFinished) {
      setStatesError();
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = null;
      }
    }
  }, [status, isOperationFinished]);

  useEffect(() => {
    if (pollingAttempts >= POLLING_ATTEMPTS_LIMIT) clearPolling();
  }, [pollingAttempts]);

  return (
    <Ctx.Provider
      value={{
        url,
        connection,
        error,
        reloadError,
        status,
        timeoutError,
        connect,
        disconnect,
        resetStates,
        clearPolling,
        setIsOperationFinished,
        getOperationResults,
        dispatchDocumentSignature: dispatchers['dispatchDocumentSignature'],
        documentSignatureState: {
          ...documentSignature,
          status: checkStatus<SignedDocument>(
            documentSignature,
            () => !!documentSignature.data?.signed
          ),
        },
        operationStartedState: operationStarted,
        idCapturedState: {
          ...idCaptured,
          status: checkStatus(idCaptured, () =>
            Boolean(Object.keys(idCaptured.data || {}).length)
          ),
        },
        selfieCapturedState: selfieCaptured,
        facialAuthenticationState: {
          ...facialAuthenticationEvaluated,
          status: checkStatus(
            facialAuthenticationEvaluated,
            () =>
              facialAuthenticationEvaluated.data?.authStatus ===
              FacialAuthenticationStatus.positive
          ),
        },
        facialExtractionState: {
          ...facialExtractionEvaluated,
          status: checkStatus(facialExtractionEvaluated, () =>
            Boolean(Object.keys(facialExtractionEvaluated.data || {}).length)
          ),
        },
        passiveLivenessState: {
          ...passiveLivenessEvaluated,
          status: checkStatus(
            passiveLivenessEvaluated,
            () =>
              passiveLivenessEvaluated.data?.diagnostic ===
              FacialLivenessDiagnostic.live
          ),
        },
        idValidatedState: {
          ...idValidated,
          status: morphologicalStatus,
        },
        civilRegistryState: {
          ...civilRegistryValidated,
          status: checkStatus(
            civilRegistryValidated,
            () => !!civilRegistryValidated.data?.summary?.facialMatching
          ),
        },
        previousOnboardingState: {
          ...previousOnboardingValidated,
          status: checkStatus(
            previousOnboardingValidated,
            () => !previousOnboardingValidated.data?.found
          ),
        },
        operationFinishedState: {
          ...operationFinished,
          status: operationFinishStatus,
        },
        operationExpiredState: operationExpired,
        personalInformationState: {
          ...personalInformationGathered,
          status: checkStatus(personalInformationGathered, () =>
            Boolean(Object.keys(personalInformationGathered.data || {}).length)
          ),
        },
      }}
    >
      {children}
    </Ctx.Provider>
  );
};

export const useData = () => useContext(Ctx);
